import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, MicOff, Loader2, X, Check, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Silence detection configuration
const SILENCE_THRESHOLD = 0.01;
const SILENCE_DURATION = 2500;
const MIN_RECORDING_DURATION = 1000;

const GrowthRecordVoiceRx = ({ 
  onEntitiesExtracted,
  patientContext,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [parsedResult, setParsedResult] = useState(null);
  const [audioLevel, setAudioLevel] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const silenceTimerRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isRecordingRef = useRef(false);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  const stopAllAudio = useCallback(() => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
    if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
    if (audioContextRef.current) audioContextRef.current.close();
  }, []);

  useEffect(() => {
    return () => stopAllAudio();
  }, [stopAllAudio]);

  const processAudio = useCallback(async () => {
    setIsProcessing(true);
    setProcessingStep('Transcribing...');
    
    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        try {
          const base64Audio = reader.result.split(',')[1];
          
          // Step 1: Transcribe
          const transcribeResponse = await axios.post(`${API}/api/ai-clinical/transcribe`, {
            audio_base64: base64Audio,
            mime_type: 'audio/webm',
            language: null,
            translate_to_english: true
          });
          
          const transcript = transcribeResponse.data.text;
          
          if (!transcribeResponse.data.success || !transcript || transcript.trim() === '') {
            toast.error('Could not transcribe audio. Please try again.');
            setIsProcessing(false);
            return;
          }
          
          // Step 2: Extract growth record entities
          setProcessingStep('Extracting measurements...');
          const extractResponse = await axios.post(`${API}/api/ai-clinical/extract-growth-record-entities`, {
            text: transcript,
            context: patientContext
          });
          
          const result = extractResponse.data;
          console.log('Growth Record Voice RX - Transcription:', transcript);
          console.log('Growth Record Voice RX - Extraction result:', result);
          result.raw_text = transcript;
          setParsedResult(result);
          setProcessingStep('');
          setIsProcessing(false);
          
          // Check if any fields were extracted
          const extractedFields = Object.keys(result).filter(
            key => key !== 'raw_text' && key !== 'error' && result[key]
          );
          if (extractedFields.length > 0) {
            toast.success(`Growth measurements extracted! (${extractedFields.length} fields)`);
          } else {
            toast.warning('No growth measurements detected in dictation');
          }
        } catch (innerError) {
          console.error('Error in transcription/extraction:', innerError);
          const errorMsg = innerError.response?.data?.detail || innerError.message || 'Unknown error';
          toast.error(`Failed: ${errorMsg}`);
          setIsProcessing(false);
          setProcessingStep('');
        }
      };
      
      reader.readAsDataURL(audioBlob);
      
    } catch (error) {
      console.error('Error processing audio:', error);
      toast.error('Failed to process dictation. Please try again.');
      setIsProcessing(false);
      setProcessingStep('');
    }
  }, [patientContext]);

  const stopRecording = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setIsRecording(false);
  }, []);

  const monitorAudioLevelRef = useRef(null);

  useEffect(() => {
    monitorAudioLevelRef.current = () => {
      if (!analyserRef.current) return;
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteTimeDomainData(dataArray);
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const value = (dataArray[i] - 128) / 128;
        sum += value * value;
      }
      const rms = Math.sqrt(sum / dataArray.length);
      setAudioLevel(rms);
      const currentlySilent = rms < SILENCE_THRESHOLD;
      
      if (isRecordingRef.current) {
        if (currentlySilent) {
          if (!silenceTimerRef.current) {
            silenceTimerRef.current = setTimeout(() => {
              const recordingDuration = Date.now() - recordingStartTimeRef.current;
              if (recordingDuration >= MIN_RECORDING_DURATION && audioChunksRef.current.length > 0) {
                stopRecording();
              }
            }, SILENCE_DURATION);
          }
        } else {
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
          }
        }
      }
      
      animationFrameRef.current = requestAnimationFrame(monitorAudioLevelRef.current);
    };
  }, [stopRecording]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        if (audioChunksRef.current.length > 0) {
          await processAudio();
        }
      };
      
      mediaRecorder.start(100);
      recordingStartTimeRef.current = Date.now();
      setIsRecording(true);
      if (monitorAudioLevelRef.current) {
        monitorAudioLevelRef.current();
      }
      
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  }, [processAudio]);

  const applyResults = () => {
    if (!parsedResult) return;
    
    if (onEntitiesExtracted) {
      onEntitiesExtracted(parsedResult);
      
      const extractedFields = Object.keys(parsedResult).filter(
        key => key !== 'raw_text' && key !== 'error' && parsedResult[key]
      );
      toast.success(`${extractedFields.length} field(s) extracted and applied`);
    }
    
    setParsedResult(null);
    setIsOpen(false);
  };

  if (disabled) return null;

  return (
    <div className="relative">
      {/* Main Dictation Button */}
      <Button
        type="button"
        variant={isOpen ? "default" : "outline"}
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 ${isOpen ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}`}
        data-testid="growth-record-voice-rx-btn"
      >
        <Mic className="w-4 h-4" />
        <span className="hidden sm:inline">Voice Rx</span>
      </Button>
      
      {/* Dictation Panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                <span className="font-semibold">Growth Record Voice Rx</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setIsOpen(false); setParsedResult(null); }}
                className="text-white hover:bg-white/20 h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-blue-200 mt-1">
              Dictate weight, height, head circumference
            </p>
          </div>
          
          {/* Recording Area */}
          <div className="p-4">
            {!parsedResult ? (
              <div className="text-center">
                {/* Recording Button */}
                <button
                  type="button"
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isProcessing}
                  className={`w-20 h-20 rounded-full flex items-center justify-center transition-all mx-auto ${
                    isRecording 
                      ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                      : isProcessing 
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                  data-testid="growth-record-voice-record-btn"
                >
                  {isProcessing ? (
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  ) : isRecording ? (
                    <MicOff className="w-8 h-8 text-white" />
                  ) : (
                    <Mic className="w-8 h-8 text-white" />
                  )}
                </button>
                
                {/* Status Text */}
                <p className="mt-3 text-sm text-gray-600">
                  {isProcessing ? processingStep : isRecording ? 'Listening... (stops on silence)' : 'Tap to start dictating'}
                </p>
                
                {/* Audio Level Indicator */}
                {isRecording && (
                  <div className="mt-3 flex justify-center gap-1">
                    {[...Array(10)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-1.5 rounded-full transition-all ${
                          audioLevel * 50 > i ? 'bg-blue-500' : 'bg-gray-200'
                        }`}
                        style={{ height: `${10 + i * 3}px` }}
                      />
                    ))}
                  </div>
                )}
                
                {/* Example Phrases */}
                {!isRecording && !isProcessing && (
                  <div className="mt-4 text-xs text-gray-500">
                    <p className="font-medium mb-1">Example phrases:</p>
                    <p className="italic">&quot;Weight 3.5 kg, height 51 cm, head 36 cm&quot;</p>
                    <p className="italic mt-1">&quot;Baby weighs 4 kilograms, length 55 centimeters&quot;</p>
                  </div>
                )}
              </div>
            ) : (
              /* Parsed Results */
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Extracted Measurements</span>
                  <Badge variant="outline" className="text-xs">
                    {Object.keys(parsedResult).filter(k => k !== 'raw_text' && k !== 'error' && parsedResult[k]).length} fields
                  </Badge>
                </div>
                
                {/* Measurements Grid */}
                <div className="grid grid-cols-3 gap-3">
                  {/* Weight */}
                  <div className={`rounded-lg p-3 text-center ${parsedResult.weight ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                    <div className="text-xs text-gray-500 mb-1">Weight</div>
                    <div className={`text-lg font-bold ${parsedResult.weight ? 'text-green-700' : 'text-gray-400'}`}>
                      {parsedResult.weight || '--'}
                    </div>
                    <div className="text-xs text-gray-500">kg</div>
                  </div>
                  
                  {/* Height */}
                  <div className={`rounded-lg p-3 text-center ${parsedResult.height ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}>
                    <div className="text-xs text-gray-500 mb-1">Height</div>
                    <div className={`text-lg font-bold ${parsedResult.height ? 'text-blue-700' : 'text-gray-400'}`}>
                      {parsedResult.height || '--'}
                    </div>
                    <div className="text-xs text-gray-500">cm</div>
                  </div>
                  
                  {/* Head Circumference */}
                  <div className={`rounded-lg p-3 text-center ${parsedResult.head_circumference ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50 border border-gray-200'}`}>
                    <div className="text-xs text-gray-500 mb-1">Head</div>
                    <div className={`text-lg font-bold ${parsedResult.head_circumference ? 'text-purple-700' : 'text-gray-400'}`}>
                      {parsedResult.head_circumference || '--'}
                    </div>
                    <div className="text-xs text-gray-500">cm</div>
                  </div>
                </div>
                
                {/* Notes */}
                {parsedResult.notes && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">Notes</div>
                    <p className="text-sm text-gray-700">{parsedResult.notes}</p>
                  </div>
                )}
                
                {/* Raw transcription */}
                {parsedResult.raw_text && (
                  <div className="bg-gray-100 rounded-lg p-2">
                    <p className="text-xs text-gray-500 font-medium mb-1">Transcribed:</p>
                    <p className="text-xs text-gray-600 italic">&quot;{parsedResult.raw_text}&quot;</p>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setParsedResult(null)}
                    className="flex-1"
                    data-testid="growth-record-voice-discard-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Discard
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={applyResults}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    data-testid="growth-record-voice-apply-btn"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Apply
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GrowthRecordVoiceRx;
